import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { authClient } from './auth-client';
import './TwoFactorAuth.css';

const TwoFactorAuth = () => {
  const { user } = useAuth();
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [step, setStep] = useState('setup'); // 'setup', 'verify', 'complete'
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    if (user) {
      // All users now have 2FA enabled by default (set during registration)
      setTwoFactorEnabled(user.twoFactorEnabled || false);
      
      // If 2FA is enabled, show the complete step
      if (user.twoFactorEnabled) {
        setStep('complete');
      }
    }
  }, [user]);

  const setupTwoFactor = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    
    try {
      // Use Better Auth's native twoFactor.enable method
      const result = await authClient.twoFactor.enable({
        password: prompt('Please enter your password to enable 2FA:')
      });

      if (result.error) {
        setError(result.error.message || 'Failed to setup two-factor authentication');
        return;
      }

      if (result.data) {
        // Better Auth returns QR code data URL and secret
        setQrCode(result.data.qrCode || result.data.totpUri);
        setSecret(result.data.secret);
        setBackupCodes(result.data.backupCodes || []);
        setStep('verify');
        setMessage('Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.), then enter the 6-digit code to verify.');
      }
    } catch (err) {
      console.error('2FA setup error:', err);
      setError(err.message || 'Failed to setup two-factor authentication');
    } finally {
      setLoading(false);
    }
  };

  const verifyTwoFactor = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    
    try {
      // Use Better Auth's native verification method
      const result = await authClient.twoFactor.verifyTotp({
        code: verificationCode
      });

      if (result.error) {
        setError(result.error.message || 'Invalid verification code. Please try again.');
        setVerificationCode('');
        return;
      }

      if (result.data && result.data.status === 'success') {
        setStep('complete');
        setTwoFactorEnabled(true);
        setMessage('🎉 Two-factor authentication has been successfully enabled! Your account is now more secure.');
      }
    } catch (err) {
      console.error('2FA verification error:', err);
      setError(err.message || 'Invalid verification code');
      setVerificationCode('');
    } finally {
      setLoading(false);
    }
  };

  const disableTwoFactor = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    
    try {
      // Ask for password confirmation before disabling
      const password = prompt('Please enter your password to disable 2FA:');
      if (!password) {
        setShowConfirmDialog(false);
        setLoading(false);
        return;
      }

      // Use Better Auth's native disable method
      const result = await authClient.twoFactor.disable({
        password
      });

      if (result.error) {
        setError(result.error.message || 'Failed to disable two-factor authentication');
        return;
      }

      setTwoFactorEnabled(false);
      setStep('setup');
      setBackupCodes([]);
      setMessage('Two-factor authentication has been disabled. Your account is less secure now.');
      setShowConfirmDialog(false);
    } catch (err) {
      console.error('2FA disable error:', err);
      setError(err.message || 'Failed to disable two-factor authentication');
    } finally {
      setLoading(false);
    }
  };

  const handleDisableConfirm = () => {
    setShowConfirmDialog(true);
  };

  const handleDisableCancel = () => {
    setShowConfirmDialog(false);
  };

  if (!user) {
    return (
      <div className="two-factor-container">
        <div className="two-factor-card">
          <h2>🔒 Two-Factor Authentication</h2>
          <p>Please sign in to manage your two-factor authentication settings.</p>
          <a href="/login" className="login-link">Sign In</a>
        </div>
      </div>
    );
  }

  return (
    <div className="two-factor-container">
      <div className="two-factor-card">
        <h2>🔒 Two-Factor Authentication</h2>
        
        {twoFactorEnabled && step !== 'setup' ? (
          <div className="two-factor-enabled">
            <div className="status-indicator enabled">
              ✅ Two-Factor Authentication is Enabled
            </div>
            <p>Your account is protected with two-factor authentication.</p>
            
            <div className="info-box" style={{ 
              background: '#eff6ff', 
              border: '1px solid #3b82f6', 
              borderRadius: '8px', 
              padding: '15px', 
              marginTop: '20px',
              marginBottom: '20px'
            }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#1e3a8a' }}>
                🔒 <strong>Mandatory Security:</strong> Two-factor authentication is required for all accounts and cannot be disabled. This ensures maximum security for your data.
              </p>
            </div>
            
            {backupCodes.length > 0 && (
              <div className="backup-codes">
                <h3>Backup Codes</h3>
                <p>Save these backup codes in a safe place. You can use them to access your account if you lose access to your authenticator app.</p>
                <div className="codes-grid">
                  {backupCodes.map((code, index) => (
                    <code key={index} className="backup-code">{code}</code>
                  ))}
                </div>
              </div>
            )}
            
            <div style={{ marginTop: '20px', padding: '15px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                ℹ️ <strong>Need help?</strong> If you lose access to your authenticator app, use one of your backup codes above. If you've lost both, contact your administrator.
              </p>
            </div>
          </div>
        ) : (
          <>
            {step === 'setup' && (
              <div className="setup-step">
                <div className="status-indicator disabled">
                  ⚠️ Two-Factor Authentication is Disabled
                </div>
                <p>Enhance your account security by enabling two-factor authentication. You'll need an authenticator app like Google Authenticator or Authy.</p>
                
                <button 
                  onClick={setupTwoFactor} 
                  className="setup-button"
                  disabled={loading}
                >
                  {loading ? 'Setting up...' : 'Enable Two-Factor Authentication'}
                </button>
              </div>
            )}
            
            {step === 'verify' && (
              <div className="verify-step">
                <h3>Scan QR Code</h3>
                <p>Scan this QR code with your authenticator app:</p>
                
                {qrCode && (
                  <div className="qr-code-container">
                    <img src={qrCode} alt="QR Code for 2FA setup" style={{width: '200px', height: '200px'}} />
                  </div>
                )}
                
                <div className="secret-key">
                  <p><strong>Manual Entry Key:</strong></p>
                  <code>{secret}</code>
                </div>
                
                <form onSubmit={verifyTwoFactor} className="verification-form">
                  <div className="form-group">
                    <label htmlFor="verificationCode">Enter 6-digit code from your app:</label>
                    <input
                      type="text"
                      id="verificationCode"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      placeholder="123456"
                      maxLength="6"
                      pattern="[0-9]{6}"
                      required
                      disabled={loading}
                    />
                  </div>
                  
                  <button 
                    type="submit" 
                    className="verify-button"
                    disabled={loading || verificationCode.length !== 6}
                  >
                    {loading ? 'Verifying...' : 'Verify & Enable'}
                  </button>
                </form>
              </div>
            )}
            
            {step === 'complete' && (
              <div className="complete-step">
                <div className="success-icon">🎉</div>
                <h3>Two-Factor Authentication Enabled!</h3>
                <p>Your account is now protected with two-factor authentication.</p>
                
                {backupCodes.length > 0 && (
                  <div className="backup-codes">
                    <h4>Important: Save Your Backup Codes</h4>
                    <p>These codes can be used to access your account if you lose your authenticator device.</p>
                    <div className="codes-grid">
                      {backupCodes.map((code, index) => (
                        <code key={index} className="backup-code">{code}</code>
                      ))}
                    </div>
                  </div>
                )}
                
                <button 
                  onClick={() => window.location.href = '/dashboard'} 
                  className="continue-button"
                >
                  Continue to Dashboard
                </button>
              </div>
            )}
          </>
        )}
        
        {message && <div className="success-message">{message}</div>}
        {error && <div className="error-message">{error}</div>}
        
        {/* Confirmation Dialog */}
        {showConfirmDialog && (
          <div className="confirmation-overlay">
            <div className="confirmation-dialog">
              <h3>⚠️ Disable Two-Factor Authentication</h3>
              <p>Are you sure you want to disable two-factor authentication? This will make your account less secure.</p>
              <div className="dialog-actions">
                <button 
                  onClick={disableTwoFactor} 
                  className="danger-button"
                  disabled={loading}
                >
                  {loading ? 'Disabling...' : 'Yes, Disable'}
                </button>
                <button 
                  onClick={handleDisableCancel} 
                  className="secondary-button"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TwoFactorAuth;